
import {
  ICertificationUseCase,
  IQuizUseCase,
  IModuleUseCase,
  INotificationsUseCase,
  IAnalyticsUseCase
} from '../usecases';
import { ICourseRepository } from '../repository';

/**
 * Learning Service
 * Orchestrates learning-related use cases for complete educational experiences
 * Handles enrollments, progress tracking, certificates, and learning analytics
 */
export class LearningService {
  constructor(
    private certificationUseCase: ICertificationUseCase,
    private quizUseCase: IQuizUseCase,
    private moduleUseCase: IModuleUseCase,
    private notificationsUseCase: INotificationsUseCase,
    private analyticsUseCase: IAnalyticsUseCase,
    private courseRepository: ICourseRepository,
    private certificationsService: any // Will be injected as CertificationsService
  ) {
    // Removed BaseUseCase
  }

  /**
   * Complete course enrollment and setup
   */
  async enrollInCourse(
    enrollmentData: {
      userId: string;
      courseId: string;
      enrollmentType: 'paid' | 'free' | 'trial';
    },
    context?: any
  ): Promise<{
    success: boolean;
    enrollment: any;
    course: any;
    firstModule?: any;
    notifications: string[];
    analytics: any;
    sideEffects?: any;
  }> {
    console.log('[LearningService.enrollInCourse] START - UserId:', enrollmentData.userId, 'CourseId:', enrollmentData.courseId, 'Type:', enrollmentData.enrollmentType);
    try {
      const notifications: string[] = [];

      // 1. Create enrollment
      console.log('[LearningService.enrollInCourse] Creating course enrollment');
      const enrollmentResult = await this.certificationUseCase.enrollStudent(
        enrollmentData.userId,
        enrollmentData.courseId
      );
      console.log('[LearningService.enrollInCourse] Enrollment created successfully');

      // 2. Get course details and first module
      console.log('[LearningService.enrollInCourse] Extracting course details and modules');
      const course = enrollmentResult.course;
      const firstModule = enrollmentResult.modules.length > 0 ? enrollmentResult.modules[0] : null;
      console.log('[LearningService.enrollInCourse] Course:', course.title, 'FirstModule:', firstModule ? firstModule.title : 'None');

      // 3. Send welcome notification
      console.log('[LearningService.enrollInCourse] Sending enrollment confirmation notification');
      try {
        await this.notificationsUseCase.sendEmailNotification(
          enrollmentData.userId,
          'enrollmentConfirmation',
          {
            courseName: course.title,
            enrollmentType: enrollmentData.enrollmentType,
            startDate: new Date().toISOString()
          }
        );
        notifications.push('Welcome email sent');
        console.log('[LearningService.enrollInCourse] Enrollment confirmation email sent successfully');
      } catch (error) {
        notifications.push('Failed to send welcome email');
        console.log('[LearningService.enrollInCourse] Failed to send enrollment confirmation:', error);
      }

      // 4. Handle enrollment side effects (course update, user update, instructor notification)
      console.log('[LearningService.enrollInCourse] Triggering enrollment side effects');
      let sideEffectsResult = null;
      try {
        if (enrollmentData.enrollmentType === 'paid') {
          sideEffectsResult = await this.certificationsService.handleSuccessfulCourseEnrollment({
            userId: enrollmentData.userId,
            courseId: enrollmentData.courseId,
            enrollmentId: enrollmentResult.enrollment.id,
            paymentAmount: course.price || 0,
            paymentMethod: 'paidEnrollment',
            sessionId: `enrollment_${Date.now()}`
          });
          console.log('[LearningService.enrollInCourse] Side effects result:', sideEffectsResult);
        } else {
          console.log('[LearningService.enrollInCourse] Skipping side effects for non-paid enrollment');
        }
      } catch (error) {
        console.error('[LearningService.enrollInCourse] Error handling side effects:', error);
        // Don't fail the enrollment if side effects fail
      }

      // 5. Track analytics
      console.log('[LearningService.enrollInCourse] Tracking enrollment analytics');
      await this.analyticsUseCase.trackCourseEnrollment(
        {
          userId: enrollmentData.userId,
          courseId: enrollmentData.courseId,
          enrollmentType: enrollmentData.enrollmentType
        },
        context
      );
      console.log('[LearningService.enrollInCourse] Analytics tracking completed');

      const analytics = {
        event: 'courseEnrollment',
        userId: enrollmentData.userId,
        courseId: enrollmentData.courseId,
        enrollmentType: enrollmentData.enrollmentType,
        courseTitle: course.title,
        courseInstructor: course.instructorId
      };

      console.log('[LearningService.enrollInCourse] SUCCESS - Enrollment completed for user:', enrollmentData.userId, 'course:', enrollmentData.courseId, 'notifications:', notifications.length);
      return {
        success: true,
        enrollment: enrollmentResult.enrollment,
        course,
        firstModule,
        notifications,
        analytics,
        sideEffects: sideEffectsResult
      };
    } catch (error) { 
      console.error('[LearningService.enrollInCourse] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Get all available courses
   */
  async getAllCourses(
    filters?: {
      category?: string;
      difficulty?: string;
      instructor?: string;
      search?: string;
      limit?: number;
      offset?: number;
    },
    context?: any
  ): Promise<{
    courses: any[];
    total: number;
    hasMore: boolean;
  }> {
    console.log('[LearningService.getAllCourses] START - Filters:', filters);
    try {
      console.log('[LearningService.getAllCourses] Calling courseRepository.getCourses');
      const courses = await this.courseRepository.getCourses(filters, context?.limit);
      console.log('[LearningService.getAllCourses] SUCCESS - Retrieved', courses.length, 'courses');

      return {
        courses,
        total: courses.length,
        hasMore: false // Simplified for now
      };
    } catch (error) {
      console.error('[LearningService.getAllCourses] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get a specific course by ID
   */
  async getCourse(
    courseId: string,
    context?: any
  ): Promise<any> {
    console.log('[LearningService.getCourse] START - CourseId:', courseId);
    try {
      console.log('[LearningService.getCourse] Calling courseRepository.getCourseById');
      const course = await this.courseRepository.getCourseById(courseId, context);
      console.log('[LearningService.getCourse] SUCCESS - Course found:', course ? 'Yes' : 'No');
      return course;
    } catch (error) {
      console.error('[LearningService.getCourse] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get courses created by the current user (owner)
   */
  async getMyCourses(
    userId: string,
    filters?: {
      status?: string;
      category?: string;
      limit?: number;
      offset?: number;
    },
    context?: any
  ): Promise<{
    courses: any[];
    total: number;
    hasMore: boolean;
  }> {
    console.log('[LearningService.getMyCourses] START - UserId:', userId, 'Filters:', filters);
    try {
      console.log('[LearningService.getMyCourses] Calling courseRepository.getCoursesByOwner');
      const courses = await this.courseRepository.getCoursesByOwner(userId, filters?.limit);
      console.log('[LearningService.getMyCourses] SUCCESS - Found', courses.length, 'courses for owner:', userId);

      return {
        courses,
        total: courses.length,
        hasMore: false
      };
    } catch (error) {
      console.error('[LearningService.getMyCourses] ERROR:', error);
      throw error;
    }
  }


  /**
   * Get course with enrollment status for a user
   */
  async getCourseWithEnrollment(
    courseId: string,
    userId: string,
    context?: any
  ): Promise<any> {
    console.log('[LearningService.getCourseWithEnrollment] START - CourseId:', courseId, 'UserId:', userId);
    try {
      console.log('[LearningService.getCourseWithEnrollment] Getting course details');
      const course = await this.courseRepository.getCourseById(courseId, context);
      console.log('[LearningService.getCourseWithEnrollment] Course found:', course ? 'Yes' : 'No');

      // Get enrollment status
      console.log('[LearningService.getCourseWithEnrollment] Checking enrollment status');
      const enrollments = await this.courseRepository.getUserEnrollments(userId);
      const enrollment = enrollments.find(e => e.courseId === courseId);
      const enrollmentStatus = enrollment ? 'enrolled' : null;
      console.log('[LearningService.getCourseWithEnrollment] Enrollment status:', enrollmentStatus);

      console.log('[LearningService.getCourseWithEnrollment] SUCCESS - CourseId:', courseId, 'EnrollmentStatus:', enrollmentStatus);
      return {
        ...course,
        enrollmentStatus,
        enrollment
      };
    } catch (error) {
      console.error('[LearningService.getCourseWithEnrollment] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get user's enrolled courses
   */
  async getUserEnrolledCourses(
    userId: string,
    context?: any
  ): Promise<any[]> {
    console.log('[LearningService.getUserEnrolledCourses] START - UserId:', userId);
    try {
      console.log('[LearningService.getUserEnrolledCourses] Getting user enrollments');
      const enrollments = await this.courseRepository.getUserEnrollments(userId);
      console.log('[LearningService.getUserEnrolledCourses] Found', enrollments.length, 'enrollments');
      
      // Debug: Log enrollment details
      enrollments.forEach((enrollment, index) => {
        console.log(`[LearningService.getUserEnrolledCourses] Enrollment ${index}:`, {
          id: enrollment.id,
          userId: enrollment.userId,
          courseId: enrollment.courseId,
          status: enrollment.enrollmentStatus,
          enrolledAt: enrollment.enrolledAt
        });
      });

      // Get course details for each enrollment
      console.log('[LearningService.getUserEnrolledCourses] Enriching enrollments with course details');
      const coursesWithProgress = await Promise.all(
        enrollments.map(async (enrollment) => {
          try {
            console.log(`[LearningService.getUserEnrolledCourses] Fetching course details for courseId:`, enrollment.courseId);
            const course = await this.courseRepository.getCourseById(enrollment.courseId, context);
            
            if (!course) {
              console.warn(`[LearningService.getUserEnrolledCourses] Course not found for courseId:`, enrollment.courseId);
              return null;
            }
            
            console.log(`[LearningService.getUserEnrolledCourses] Found course:`, course.title);
            return {
              ...course,
              enrollment,
              progress: enrollment.progressPercentage || 0
            };
          } catch (error) {
            console.error(`[LearningService.getUserEnrolledCourses] Error fetching course for enrollment ${enrollment.id}:`, error);
            return null;
          }
        })
      );
      
      // Filter out null results
      const validCourses = coursesWithProgress.filter(course => course !== null);
      console.log('[LearningService.getUserEnrolledCourses] Enriched', validCourses.length, 'courses with progress (filtered from', coursesWithProgress.length, 'total)');

      console.log('[LearningService.getUserEnrolledCourses] SUCCESS - UserId:', userId, 'EnrolledCourses:', validCourses.length);
      return validCourses;
    } catch (error) {
      console.error('[LearningService.getUserEnrolledCourses] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get course progress for a user
   */
  async getCourseProgress(
    userId: string,
    courseId: string,
    context?: any
  ): Promise<any> {
    console.log('[LearningService.getCourseProgress] START - UserId:', userId, 'CourseId:', courseId);
    try {
      console.log('[LearningService.getCourseProgress] Calling certificationUseCase.getStudentProgress');
      const progress = await this.certificationUseCase.getStudentProgress(userId, courseId);
      console.log('[LearningService.getCourseProgress] SUCCESS - Progress retrieved for user:', userId, 'course:', courseId);
      return progress;
    } catch (error) {
      console.error('[LearningService.getCourseProgress] ERROR:', error);
      throw error;
    }
  }

  /**
   * Track lesson completion and update progress
   */
  async completeLesson(
    completionData: {
      userId: string;
      lessonId: string;
      courseId: string;
      timeSpentSeconds: number;
      completionPercentage: number;
    },
    context?: any
  ): Promise<void> {
    console.log('[LearningService.completeLesson] START - UserId:', completionData.userId, 'LessonId:', completionData.lessonId, 'CourseId:', completionData.courseId);
    try {
      // Method implementation
      console.log('[LearningService.completeLesson] Processing lesson completion - Time spent:', completionData.timeSpentSeconds, 'seconds, Completion:', completionData.completionPercentage + '%');
      console.log('[LearningService.completeLesson] SUCCESS - Lesson completion processed');
        } catch (error) {
      console.error('[LearningService.completeLesson] ERROR:', error);
      throw error;
    }
  }

  /**
   * Quiz with its questions for taking (answer keys stripped on the server).
   */
  async getQuizForTaking(quizId: string, userId: string) {
    return this.quizUseCase.getQuizForTaking(quizId, userId);
  }

  /**
   * Take quiz and process results
   */
  async takeQuiz(
    quizData: {
      userId: string;
      quizId: string;
      courseId: string;
      answers: Record<string, any>;
      timeSpentSeconds: number;
    },
    context?: any
  ): Promise<{
    success: boolean;
    results: any;
    certificate?: any;
    progress: any;
    notifications: string[];
    analytics: any;
    nextActions: string[];
  }> {
    console.log('[LearningService.takeQuiz] START - UserId:', quizData.userId, 'QuizId:', quizData.quizId, 'CourseId:', quizData.courseId, 'TimeSpent:', quizData.timeSpentSeconds);
    try {
      const notifications: string[] = [];
      const nextActions: string[] = [];

      // 1. Submit quiz attempt
      console.log('[LearningService.takeQuiz] Submitting quiz attempt');
      const quizResult = await this.certificationUseCase.submitQuizAttempt(
        quizData.userId,
        quizData.quizId,
        quizData.answers
      );
      console.log('[LearningService.takeQuiz] Quiz submitted - Score:', quizResult.score, 'Passed:', quizResult.passed);

      // The attempt is recorded and graded on the server by this point.
      // Nothing below may turn that into an error for the learner.

      // 2. Track quiz analytics
      console.log('[LearningService.takeQuiz] Tracking quiz analytics');
      await this.analyticsUseCase.trackQuizAttempt(
        {
          userId: quizData.userId,
          quizId: quizData.quizId,
          courseId: quizData.courseId,
          score: quizResult.score,
          totalQuestions: 10, // Would be dynamic
          timeSpentSeconds: quizData.timeSpentSeconds,
          passed: quizResult.passed
        },
        context
      ).catch((error: unknown) => console.warn('[LearningService.takeQuiz] Analytics tracking failed:', error));
      console.log('[LearningService.takeQuiz] Analytics tracking completed');

      // 3. Get updated progress
      console.log('[LearningService.takeQuiz] Getting updated course progress');
      const progressResult = await this.certificationUseCase
        .getStudentProgress(quizData.userId, quizData.courseId)
        .catch((error: unknown) => {
          console.warn('[LearningService.takeQuiz] Progress lookup failed:', error);
          return null;
        });
      console.log('[LearningService.takeQuiz] Progress updated');

      // 4. Handle certificate generation
      const certificate = quizResult.certificate;
      if (certificate) {
        console.log('[LearningService.takeQuiz] Certificate earned, sending notification');
        try {
          await this.notificationsUseCase.sendEmailNotification(
            quizData.userId,
            'enrollmentConfirmation',
            {
              certificateNumber: certificate.certificateNumber,
              courseName: progressResult?.course?.title,
              completionDate: certificate.issuedAt,
              grade: certificate.completionStatus
            }
          );
          notifications.push('Certificate earned notification sent');
          nextActions.push('certificateEarned');
          console.log('[LearningService.takeQuiz] Certificate notification sent successfully');
        } catch (error) {
          notifications.push('Failed to send certificate notification');
          console.log('[LearningService.takeQuiz] Failed to send certificate notification:', error);
        }
      }

      // 5. Determine next actions
      console.log('[LearningService.takeQuiz] Determining next actions based on quiz result');
      if (quizResult.passed) {
        nextActions.push('proceedToNextModule');
        console.log('[LearningService.takeQuiz] Quiz passed - can proceed to next module');

        if (certificate) {
          nextActions.push('courseCompleted');
          console.log('[LearningService.takeQuiz] Course completed with certificate');
        }
      } else {
        nextActions.push('retryQuiz');
        console.log('[LearningService.takeQuiz] Quiz failed - retry required');
      }

      const analytics = {
        event: 'quizAttempt',
        userId: quizData.userId,
        quizId: quizData.quizId,
        courseId: quizData.courseId,
        score: quizResult.score,
        passed: quizResult.passed,
        timeSpentSeconds: quizData.timeSpentSeconds,
        certificateEarned: !!certificate
      };

      console.log('[LearningService.takeQuiz] SUCCESS - QuizId:', quizData.quizId, 'Score:', quizResult.score, 'Passed:', quizResult.passed, 'NextActions:', nextActions.length);
      return {
        success: true,
        results: quizResult,
        certificate,
        progress: progressResult,
        notifications,
        analytics,
        nextActions
      };
    } catch (error) { 
      console.error('[LearningService.takeQuiz] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Get comprehensive learning dashboard
   */
  async getLearningDashboard(
    userId: string,
    context?: any
  ): Promise<{
    enrolledCourses: any[];
    completedCourses: any[];
    certificates: any[];
    currentProgress: any[];
    upcomingDeadlines: any[];
    recentAchievements: any[];
    learningStats: {
      totalCoursesEnrolled: number;
      totalCoursesCompleted: number;
      totalCertificates: number;
      averageScore: number;
      totalTimeSpent: number;
      currentStreak: number;
    };
    recommendations: any[];
  }> {
    console.log('[LearningService.getLearningDashboard] START - UserId:', userId);
    try {
      // Get student certifications (includes enrolled courses)
      console.log('[LearningService.getLearningDashboard] Getting student certifications and enrollments');
      const studentCertifications = await this.certificationUseCase.getStudentCertifications(userId);
      console.log('[LearningService.getLearningDashboard] Retrieved certifications and enrollments');

      // Mock additional data - in real implementation would aggregate from multiple sources
      const completedCourses = studentCertifications.enrolledCourses.filter(
        course => course.certificate
      );

      const currentProgress = studentCertifications.enrolledCourses.filter(
        course => !course.certificate
      );

      const certificates = studentCertifications.completedCertifications;

      const upcomingDeadlines = [
        {
          courseId: 'course1',
          courseName: 'Advanced Techniques',
          deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
          type: 'moduleDeadline',
          priority: 'high'
        }
      ];

      const recentAchievements = [
        {
          id: 'achievement1',
          title: 'First Lesson Completed',
          description: 'Completed your first lesson',
          earnedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          type: 'milestone'
        },
        {
          id: 'achievement2',
          title: 'Quiz Master',
          description: 'Scored 100% on a quiz',
          earnedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          type: 'performance'
        }
      ];

      const learningStats = {
        totalCoursesEnrolled: studentCertifications.enrolledCourses.length,
        totalCoursesCompleted: completedCourses.length,
        totalCertificates: certificates.length,
        averageScore: 87.5,
        totalTimeSpent: 14400, // 4 hours in seconds
        currentStreak: 5
      };

      const recommendations = [
        {
          type: 'course',
          id: 'courseRec1',
          title: 'Recommended: Contemporary Dance Basics',
          reason: 'Based on your interest in Advanced Techniques',
          priority: 'high'
        },
        {
          type: 'resource',
          id: 'resourceRec1',
          title: 'Recommended: Warm-up Routine Video',
          reason: 'Complements your current learning',
          priority: 'medium'
        }
      ];

      console.log('[LearningService.getLearningDashboard] SUCCESS - UserId:', userId, 'EnrolledCourses:', studentCertifications.enrolledCourses.length, 'CompletedCourses:', completedCourses.length, 'Certificates:', certificates.length);
      return {
        enrolledCourses: studentCertifications.enrolledCourses,
        completedCourses,
        certificates,
        currentProgress,
        upcomingDeadlines,
        recentAchievements,
        learningStats,
        recommendations
      };
    } catch (error) { 
      console.error('[LearningService.getLearningDashboard] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Generate learning path recommendations
   */
  async getLearningPath(
    userId: string,
    currentCourseId?: string,
    context?: any
  ): Promise<{
    currentCourse?: any;
    nextRecommendedCourses: any[];
    prerequisiteCourses: any[];
    skillGapAnalysis: any[];
    estimatedCompletionTime: number;
    difficultyProgression: string[];
  }> {
    console.log('[LearningService.getLearningPath] START - UserId:', userId, 'CurrentCourseId:', currentCourseId);
    try {
      // Mock learning path - would use ML/AI in real implementation
      const learningPath = {
        currentCourse: currentCourseId ? {
          id: currentCourseId,
          title: 'Advanced Dance Techniques',
          difficulty: 'intermediate',
          estimatedHours: 40,
          progress: 65
        } : undefined,
        nextRecommendedCourses: [
          {
            id: 'courseNext1',
            title: 'Professional Performance Techniques',
            difficulty: 'advanced',
            estimatedHours: 60,
            prerequisites: [currentCourseId],
            reason: 'Natural progression from current course'
          },
          {
            id: 'courseNext2',
            title: 'Dance Teaching Methodology',
            difficulty: 'intermediate',
            estimatedHours: 30,
            prerequisites: [],
            reason: 'Complementary skill development'
          }
        ],
        prerequisiteCourses: [
          {
            id: 'coursePrereq1',
            title: 'Dance Fundamentals',
            difficulty: 'beginner',
            estimatedHours: 20,
            completed: true,
            completionDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)
          }
        ],
        skillGapAnalysis: [
          {
            skill: 'Advanced Turns',
            currentLevel: 'intermediate',
            requiredLevel: 'advanced',
            gap: 'medium',
            recommendedResources: ['resource1', 'resource2']
          },
          {
            skill: 'Performance Preparation',
            currentLevel: 'beginner',
            requiredLevel: 'intermediate',
            gap: 'high',
            recommendedResources: ['resource3']
          }
        ],
        estimatedCompletionTime: 120, // hours
        difficultyProgression: ['beginner', 'intermediate', 'advanced', 'expert']
      };

      console.log('[LearningService.getLearningPath] SUCCESS - UserId:', userId, 'NextRecommendations:', learningPath.nextRecommendedCourses.length, 'SkillGaps:', learningPath.skillGapAnalysis.length, 'EstimatedTime:', learningPath.estimatedCompletionTime, 'hours');
      return learningPath;
    } catch (error) { 
      console.error('[LearningService.getLearningPath] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Get course/module progress with detailed analytics
   */
  async getDetailedProgress(
    userId: string,
    courseId: string,
    context?: any
  ): Promise<{
    course: any;
    overallProgress: {
      percentage: number;
      completedModules: number;
      totalModules: number;
      timeSpent: number;
      estimatedTimeRemaining: number;
    };
    moduleProgress: Array<{
      module: any;
      progress: number;
      lessons: Array<{
        lesson: any;
        completed: boolean;
        timeSpent: number;
        score?: number;
      }>;
      quiz?: {
        quiz: any;
        attempts: number;
        bestScore: number;
        passed: boolean;
        lastAttempt: Date;
      };
    }>;
    learningInsights: {
      strengths: string[];
      areasForImprovement: string[];
      studyPatterns: any[];
      recommendedPace: string;
    };
  }> {
    console.log('[LearningService.getDetailedProgress] START - UserId:', userId, 'CourseId:', courseId);
    try {
      // Get student progress
      console.log('[LearningService.getDetailedProgress] Getting student progress');
      const progressResult = await this.certificationUseCase.getStudentProgress(
        userId,
        courseId
      );
      console.log('[LearningService.getDetailedProgress] Progress retrieved - Overall:', progressResult.overallProgress.progressPercentage + '%');

      // Mock detailed progress data
      const detailedProgress = {
        course: progressResult.course,
        overallProgress: {
          percentage: progressResult.overallProgress.progressPercentage,
          completedModules: progressResult.overallProgress.completedModules,
          totalModules: progressResult.overallProgress.totalModules,
          timeSpent: 7200, // 2 hours in seconds
          estimatedTimeRemaining: 10800 // 3 hours in seconds
        },
        moduleProgress: progressResult.modules.map((module, index) => ({
          module: module.module,
          progress: index === 0 ? 100 : index === 1 ? 75 : 25, // Mock progress
          lessons: module.lessons.map((lesson, lessonIndex) => ({
            lesson,
            completed: lessonIndex < 2, // Mock completion
            timeSpent: Math.floor(Math.random() * 1800) + 600, // 10-40 minutes
            score: lessonIndex < 2 ? Math.floor(Math.random() * 20) + 80 : undefined // 80-100
          })),
          quiz: module.quizzes.length > 0 ? {
            quiz: module.quizzes[0],
            attempts: Math.floor(Math.random() * 3) + 1,
            bestScore: Math.floor(Math.random() * 20) + 80,
            passed: true,
            lastAttempt: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000)
          } : undefined
        })),
        learningInsights: {
          strengths: [
            'Consistent study schedule',
            'Strong performance in practical exercises',
            'Good retention of theoretical concepts'
          ],
          areasForImprovement: [
            'Quiz time management',
            'Video tutorial completion rate',
            'Peer discussion participation'
          ],
          studyPatterns: [
            {
              pattern: 'Evenings are most productive',
              evidence: '85% of completed lessons done between 6-9 PM',
              recommendation: 'Schedule difficult topics for evening sessions'
            },
            {
              pattern: 'Weekends show higher engagement',
              evidence: '2.5x more activity on weekends',
              recommendation: 'Use weekends for comprehensive review'
            }
          ],
          recommendedPace: 'You are progressing well. Consider maintaining current pace with 2-3 hours daily.'
        }
      };

      console.log('[LearningService.getDetailedProgress] SUCCESS - UserId:', userId, 'CourseId:', courseId, 'OverallProgress:', detailedProgress.overallProgress.percentage + '%', 'ModulesProgress:', detailedProgress.moduleProgress.length);
      return detailedProgress;
    } catch (error) { 
      console.error('[LearningService.getDetailedProgress] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Generate study plan and schedule
   */
  async generateStudyPlan(
    userId: string,
    courseId: string,
    preferences: {
      dailyTimeCommitment: number; // minutes
      weeklyDays: number;
      preferredTimes: string[];
      learningStyle: 'visual' | 'auditory' | 'kinesthetic' | 'mixed';
    },
    context?: any
  ): Promise<{
    studyPlan: {
      totalDuration: number; // weeks
      weeklySchedule: Array<{
        week: number;
        modules: string[];
        dailyGoals: Array<{
          day: string;
          activities: Array<{
            type: 'lesson' | 'quiz' | 'practice' | 'review';
            itemId: string;
            title: string;
            duration: number; // minutes
            priority: 'high' | 'medium' | 'low';
          }>;
        }>;
      }>;
    };
    personalizedRecommendations: string[];
    progressTracking: {
      milestones: Array<{
        week: number;
        description: string;
        measurable: boolean;
      }>;
      assessmentPoints: Array<{
        week: number;
        type: 'quiz' | 'practical' | 'peerReview';
        description: string;
      }>;
    };
  }> {
    console.log('[LearningService.generateStudyPlan] START - UserId:', userId, 'CourseId:', courseId, 'DailyCommitment:', preferences.dailyTimeCommitment, 'minutes, LearningStyle:', preferences.learningStyle);
    try {
      // Mock study plan generation
      const studyPlan = {
        totalDuration: 8,
        weeklySchedule: Array.from({ length: 8 }, (_, weekIndex) => ({
          week: weekIndex + 1,
          modules: [`Module ${weekIndex + 1}`],
          dailyGoals: ['monday', 'wednesday', 'friday'].map(day => ({
            day,
            activities: [
              {
                type: 'lesson' as const,
                itemId: `lesson_${weekIndex}_${day}_1`,
                title: `Lesson ${weekIndex + 1}.${day === 'monday' ? 1 : day === 'wednesday' ? 2 : 3}`,
                duration: 45,
                priority: 'high' as const
              },
              {
                type: 'practice' as const,
                itemId: `practice_${weekIndex}_${day}`,
                title: `Practice Session ${weekIndex + 1}`,
                duration: 30,
                priority: 'medium' as const
              }
            ]
          }))
        }))
      };

      const personalizedRecommendations = [
        `Based on your ${preferences.learningStyle} learning style, focus on ${preferences.learningStyle === 'visual' ? 'video demonstrations' : preferences.learningStyle === 'auditory' ? 'audio explanations' : 'hands-on practice'}`,
        `With ${preferences.dailyTimeCommitment} minutes daily commitment, you'll complete the course in approximately ${Math.ceil(480 / (preferences.dailyTimeCommitment / 60))} days`,
        'Schedule quizzes for Wednesday sessions when retention is typically highest',
        'Include 10-minute review sessions at the end of each week'
      ];

      const progressTracking = {
        milestones: [
          { week: 2, description: 'Complete first module', measurable: true },
          { week: 4, description: 'Master basic techniques', measurable: true },
          { week: 6, description: 'Apply techniques in combinations', measurable: true },
          { week: 8, description: 'Course completion and certification', measurable: true }
        ],
        assessmentPoints: [
          { week: 2, type: 'quiz' as const, description: 'Module 1 Knowledge Check' },
          { week: 4, type: 'practical' as const, description: 'Technique Demonstration' },
          { week: 6, type: 'quiz' as const, description: 'Comprehensive Assessment' },
          { week: 8, type: 'peerReview' as const, description: 'Final Project Review' }
        ]
      };

      console.log('[LearningService.generateStudyPlan] SUCCESS - UserId:', userId, 'CourseId:', courseId, 'TotalDuration:', studyPlan.totalDuration, 'weeks, Recommendations:', personalizedRecommendations.length, 'Milestones:', progressTracking.milestones.length);
      return {
        studyPlan,
        personalizedRecommendations,
        progressTracking
      };
    } catch (error) { 
      console.error('[LearningService.generateStudyPlan] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Get students for an instructor
   * A student is someone who has purchased curriculum resources OR enrolled in certifications
   */
  async getMyStudents(instructorId?: string): Promise<any[]> {
    console.log('[LearningService.getMyStudents] START - InstructorId:', instructorId);
    try {
      if (!instructorId) {
        console.log('[LearningService.getMyStudents] No instructor ID provided');
        return [];
      }

      console.log('[LearningService.getMyStudents] Getting instructor courses');
      // Get all courses by this instructor
      const instructorCourses = await this.courseRepository.getCoursesByInstructor(instructorId);
      console.log('[LearningService.getMyStudents] Found', instructorCourses.length, 'instructor courses');

      console.log('[LearningService.getMyStudents] Getting course enrollments');
      // Get all enrollments for instructor's courses
      const allStudentIds = new Set<string>();
      for (const course of instructorCourses) {
        const enrollments = await this.courseRepository.getCourseEnrollments(course.id);
        enrollments.forEach(enrollment => allStudentIds.add(enrollment.userId));
      }
      console.log('[LearningService.getMyStudents] Found', allStudentIds.size, 'unique student IDs from course enrollments');

      // TODO: Also get students who purchased curriculum resources
      // This would require checking resource purchases/downloads
      console.log('[LearningService.getMyStudents] TODO: Add students from curriculum resource purchases');

      console.log('[LearningService.getMyStudents] Getting student details and enrollment info');
      // Get user details for all students with enrollment information
      const studentsWithDetails = await Promise.all(
        Array.from(allStudentIds).map(async (studentId) => {
          try {
            const user = await this.userRepository.getUserById(studentId);
            if (!user) return null;
            
            // Get enrollment info for this student
            const enrollments = await this.courseRepository.getUserEnrollments(studentId);
            const instructorEnrollments = enrollments.filter(enrollment => 
              instructorCourses.some(course => course.id === enrollment.courseId)
            );
            
            // Calculate stats
            const completedEnrollments = instructorEnrollments.filter(e => e.enrollmentStatus === 'completed');
            const averageProgress = instructorEnrollments.length > 0 
              ? Math.round(instructorEnrollments.reduce((sum, e) => sum + (e.progressPercentage || 0), 0) / instructorEnrollments.length)
              : 0;
            
            // Get most recent activity
            const lastActivity = instructorEnrollments.length > 0 
              ? new Date(Math.max(...instructorEnrollments.map(e => 
                  e.enrolledAt ? new Date(e.enrolledAt.seconds * 1000).getTime() : 0
                )))
              : new Date();
              
            const enrollmentDate = instructorEnrollments[0] 
              ? new Date(instructorEnrollments[0].enrolledAt.seconds * 1000)
              : new Date();
            
            return {
              id: user.id,
              displayName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
              email: user.email,
              enrolledCourses: instructorEnrollments.length,
              completedCourses: completedEnrollments.length,
              averageProgress,
              lastActivity,
              enrollmentDate,
              isActive: user.isActive !== false
            };
          } catch (error) {
            console.error('[LearningService.getMyStudents] Error processing student:', studentId, error);
            return null;
          }
        })
      );

      // Filter out null results
      const validStudents = studentsWithDetails.filter(student => student !== null);
      console.log('[LearningService.getMyStudents] SUCCESS - Found', validStudents.length, 'students with details');
      
      return validStudents;
    } catch (error) {
      console.error('[LearningService.getMyStudents] ERROR:', error);
      throw error;
    }
  }
}
